import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { doTrackedRedirect } from "../lib/track";
import { toast } from "sonner";import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Coins, GaugeCircle, FileText, Zap, ArrowRight, ShieldCheck, ArrowUpRight, Globe2, Layers, BadgeCheck, UserCheck, FileCheck2 } from "lucide-react";
import MobileCaptureDialog from "../components/MobileCaptureDialog";
import CreditSeaSmartDialog from "../components/CreditSeaSmartDialog";
import ShareButton from "../components/ShareButton";

const PRESET_AMOUNTS = [
  { label: "₹1L",  value: 100000 },
  { label: "₹5L",  value: 500000 },
  { label: "₹10L", value: 1000000 },
  { label: "₹25L", value: 2500000 },
];

const STATS = [
  { icon: Coins,        top: "Instant",  bottom: "Approval" },
  { icon: GaugeCircle,  top: "Lowest",   bottom: "Interest Rates" },
  { icon: FileText,     top: "100%",     bottom: "Paperless" },
  { icon: Zap,          top: "24 hrs",   bottom: "Disbursal" },
];

const TRUST_LOGOS = [
  { name: "Poonawalla Fincorp", domain: "poonawallafincorp.com" },
  { name: "HDFC Bank",          domain: "hdfcbank.com" },
  { name: "ICICI Bank",         domain: "icicibank.com" },
  { name: "Axis Bank",          domain: "axisbank.com" },
  { name: "Kotak",              domain: "kotak.com" },
  { name: "Bajaj Finserv",      domain: "bajajfinserv.in" },
  { name: "Tata Capital",       domain: "tatacapital.com" },
  { name: "IDFC First",         domain: "idfcfirstbank.com" },
  { name: "CASHe",              domain: "cashe.co.in" },
];

function fmt(n) {
  if (!n) return "";
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(2).replace(/\.?0+$/, "")}Cr`;
  if (n >= 100_000)    return `₹${(n / 100_000).toFixed(2).replace(/\.?0+$/, "")}L`;
  if (n >= 1_000)      return `₹${(n / 1_000).toFixed(0)}K`;
  return `₹${n}`;
}

export default function LoansLanding() {
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(500000);
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [top, setTop] = useState(null);
  // Mobile capture — banking clicks MUST collect phone before redirect so we
  // can pass it to lender for eligibility + attribute the conversion.
  const [mobileCapture, setMobileCapture] = useState(null);
  // CreditSea Smart uses a dedicated deep-integration dialog (mobile → KYC
  // → POST /api/creditsea/lead). Every other lender uses MobileCaptureDialog.
  const [smartOpen, setSmartOpen] = useState(false);

  const isCreditSeaSmart = (p) =>
    (p?.provider || "").replace(/\s+/g, "").toLowerCase() === "creditseasmart";

  // Also expose loan-club-only banking products on load so the grid is
  // populated pre-submit (channel=loans_only surfaces Poonawalla + Ram Fincorp).
  useEffect(() => {
    api.get("/products", { params: { type: "banking", channel: "loans_only" } })
      .then((r) => setMatches(r.data || []))
      .catch(() => {});
  }, []);

  // SEO — set the tab title + meta description for this informational
  // loan-education page so it ranks for queries like "online loan india".
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Find the Right Online Loan for Your Financial Needs · Loan Express";
    const desc =
      "Compare online loan options from RBI-registered NBFCs. Check eligibility, " +
      "review rates and repayment terms, and apply in minutes — all on Offer Express.";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", desc);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const clean = phone.replace(/\D/g, "").slice(-10);
    if (clean.length !== 10) return toast.error("Enter a valid 10-digit mobile number");
    if (!amount || amount < 1000) return toast.error("Choose a loan amount");
    setLoading(true);
    try {
      const { data } = await api.post("/loans/match", { phone: clean, loan_amount: amount });
      setMatches(data.matches || []);
      setTop(data.top || null);
      if (data.top?.tracked_redirect_url) {
        toast.success("Best match found — redirecting…");
        // Same-tab redirect (window.open after `await` is blocked by popup
        // blockers in Chrome/Safari, which is why the top form used to look
        // like nothing happened after Check Eligibility).
        window.location.href = data.top.tracked_redirect_url;
      } else if ((data.matches || []).length) {
        toast.success(`${data.matches.length} lenders match your requirement`);
      } else {
        toast.info("Showing all lenders");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const applyDirect = (p) => (e) => {
    e.preventDefault();
    // CreditSea Smart → open the deep-integration KYC dialog instead of
    // the plain mobile capture. Everything else → standard mobile dialog.
    if (isCreditSeaSmart(p)) {
      setSmartOpen(true);
      return;
    }
    setMobileCapture(p);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Nav */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/10 border border-white/20 grid place-items-center">
              <Coins className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-bold tracking-tight">loan express</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-white/50">by offer express</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#lenders" className="hover:text-white">Lenders</a>
            <a href="#how" className="hover:text-white">How it works</a>
            <a href="#learn" className="hover:text-white">Learn</a>
            <a href="https://offer.express" className="hover:text-white">Offer Express</a>
          </nav>
          <a
            href="#eligibility"
            data-testid="header-check-eligibility"
            className="bg-white text-black text-sm font-semibold rounded-full px-4 py-2 inline-flex items-center gap-1.5"
          >
            Check Eligibility <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(1000px_400px_at_20%_0%,rgba(0,85,255,0.25),transparent),radial-gradient(600px_300px_at_100%_100%,rgba(255,255,255,0.06),transparent)]" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-2 gap-14 relative">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 mb-6">Loan Express by Offer Express</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter leading-[1.05]">
              Your Loan Journey<br />Starts Here.
            </h1>
            <p className="mt-6 text-white/70 text-lg max-w-md">
              Compare loan offers from India&apos;s leading banks and NBFCs — all in one place. Cashback on disbursal.
            </p>
            <div className="mt-12 grid grid-cols-4 gap-4 max-w-lg">
              {STATS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 rounded-full border border-white/20 mx-auto grid place-items-center mb-3">
                      <Icon className="w-5 h-5 text-white/80" />
                    </div>
                    <div className="text-sm font-semibold">{s.top}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1">{s.bottom}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Eligibility form */}
          <div id="eligibility" className="bg-white/[0.04] border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
            <div className="font-display text-2xl font-semibold tracking-tight">Check Your Eligibility</div>
            <div className="text-sm text-white/60 mt-1">Get started in less than 2 minutes</div>
            <form className="mt-6 space-y-5" onSubmit={submit}>
              <div>
                <label className="text-xs text-white/60 mb-2 block">Mobile Number</label>
                <div className="flex items-center bg-white/5 border border-white/15 rounded-lg overflow-hidden focus-within:border-white/40 transition-colors">
                  <span className="px-3 text-white/60 text-sm border-r border-white/15">+91</span>
                  <input
                    data-testid="loans-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter Mobile Number"
                    inputMode="numeric"
                    maxLength={13}
                    className="flex-1 bg-transparent px-3 py-3 text-sm outline-none placeholder-white/40"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-white/60 mb-2 block">Loan Amount</label>
                <div className="bg-white/5 border border-white/15 rounded-lg overflow-hidden focus-within:border-white/40 transition-colors">
                  <input
                    data-testid="loans-amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value || "0", 10)))}
                    min={1000}
                    className="w-full bg-transparent px-4 py-3 text-lg font-semibold outline-none"
                  />
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {PRESET_AMOUNTS.map((p) => (
                    <button
                      type="button"
                      key={p.value}
                      onClick={() => setAmount(p.value)}
                      className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                        amount === p.value
                          ? "bg-white text-black border-white"
                          : "border-white/20 text-white/70 hover:border-white/50"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="loans-submit"
                className="w-full h-12 bg-white text-black hover:bg-white/90 rounded-lg text-sm font-semibold"
              >
                {loading ? "Checking..." : (<>Check Eligibility <ArrowRight className="w-4 h-4 ml-1" /></>)}
              </Button>
              <div className="text-xs text-white/50 inline-flex items-center gap-1.5 justify-center w-full">
                <ShieldCheck className="w-3.5 h-3.5" /> No impact on your credit score
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-white/[0.03] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
          <div className="text-center text-white/70 text-sm mb-6">Trusted by 1,00,000+ customers across India</div>
          <div className="grid grid-cols-3 md:grid-cols-9 gap-6 items-center">
            {TRUST_LOGOS.map((l) => (
              <img
                key={l.domain}
                src={`https://www.google.com/s2/favicons?domain=${l.domain}&sz=256`}
                alt={l.name}
                className="w-8 h-8 object-contain mx-auto opacity-80 hover:opacity-100 transition-opacity"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* LENDER GRID — commerce-style */}
      <section id="lenders" className="bg-neutral-50 text-black">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                {top ? "Top match" : "Handpicked lenders"} {amount ? `for ${fmt(amount)}` : ""}
              </div>
              <h2 className="font-display text-4xl font-bold tracking-tighter">
                {top ? "Best match — apply now" : "Compare loan offers"}
              </h2>
              <p className="text-neutral-600 mt-2 max-w-lg text-sm">
                Cashback on disbursal · 100% digital · Instant approval on select lenders.
              </p>
            </div>
          </div>

          {matches.length === 0 && (
            <div className="border border-dashed border-black/10 rounded-xl p-10 text-center text-sm text-neutral-500">
              No lenders configured yet. Add banking products via <b>Admin → Products</b>.
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {matches.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-black/10 hover:border-black hover:shadow-md transition-all overflow-hidden flex flex-col">
                <div className="aspect-[16/9] overflow-hidden bg-white flex items-center justify-center border-b border-black/5">
                  {p.image_url && <img src={p.image_url} alt={p.title} className="max-w-[70%] max-h-[70%] object-contain" />}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-1">{p.provider}</div>
                  <div className="font-display text-lg font-semibold tracking-tight leading-snug">{p.title}</div>
                  {p.subtitle && <div className="text-xs text-neutral-600 mt-1">{p.subtitle}</div>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {p.interest_rate && <span className="text-[11px] rounded-full bg-neutral-100 px-2.5 py-1">{p.interest_rate}</span>}
                    {p.tenure && <span className="text-[11px] rounded-full bg-neutral-100 px-2.5 py-1">{p.tenure}</span>}
                  </div>
                  {p.cashback_rate && (
                    <div className="mt-3 text-xs font-semibold text-[#0055FF]">{p.cashback_rate}</div>
                  )}
                  <button
                    type="button"
                    onClick={applyDirect(p)}
                    data-testid={`lender-apply-${p.id}`}
                    className="mt-5 h-11 rounded-lg bg-black text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-neutral-800 transition-colors"
                  >
                    {p.cta_label || "Apply Now"} <ArrowUpRight className="w-4 h-4" />
                  </button>
                  <div className="mt-2">
                    <ShareButton product={p} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INFORMATIONAL CONTENT — SEO-oriented copy about online loans.
          Kept intentionally editorial (not a sales pitch) so the page ranks
          for informational queries and educates first-time borrowers. */}
      <section id="learn" className="bg-white text-black border-t border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Learn</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-[1.05]">
              Find the right online loan for your financial needs.
            </h2>
            <p className="mt-5 text-neutral-600 text-lg leading-relaxed">
              Need funds for an important expense or personal requirement?
              Offer Express makes it easy to explore online loan options and
              understand the key terms before you apply.
            </p>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              Compare available options, check eligibility, understand
              repayment terms and review applicable fees — all from the
              convenience of your device.
            </p>
          </div>

          {/* Why explore an online loan — 4-column benefit grid */}
          <div className="mt-14">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Why online</div>
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
              Why explore an online loan?
            </h3>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Globe2,     title: "Easy Online Access",       body: "Explore loan options from anywhere." },
                { icon: Zap,        title: "Simple Process",           body: "Follow a straightforward digital application journey." },
                { icon: Layers,     title: "Multiple Options",         body: "Find solutions based on your financial requirements." },
                { icon: BadgeCheck, title: "Transparent Information",  body: "Review rates, fees, repayment terms and eligibility before proceeding." },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    data-testid={`why-online-${i}`}
                    className="border border-black/10 rounded-xl p-6 hover:border-black transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-neutral-100 grid place-items-center mb-4">
                      <Icon className="w-5 h-5 text-black" />
                    </div>
                    <div className="font-display font-semibold text-base mb-1.5">{b.title}</div>
                    <div className="text-sm text-neutral-600 leading-relaxed">{b.body}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Eligibility + Documents — 2-column cards */}
          <div className="mt-14 grid md:grid-cols-2 gap-4">
            <div data-testid="eligibility-card" className="border border-black/10 rounded-xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-neutral-100 grid place-items-center">
                  <UserCheck className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight">Check your eligibility</h3>
              </div>
              <p className="text-neutral-600 leading-relaxed">
                Online loan eligibility depends on the lender and loan
                product. Factors may include income, employment, credit
                history, age and existing financial commitments.
              </p>
            </div>
            <div data-testid="documents-card" className="border border-black/10 rounded-xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-neutral-100 grid place-items-center">
                  <FileCheck2 className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight">Documents you may need</h3>
              </div>
              <p className="text-neutral-600 leading-relaxed">
                Depending on the lender, you may need identity and address
                proof, income or employment details, bank information and
                other supporting documents.
              </p>
            </div>
          </div>

          {/* Soft CTA back to the top form */}
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a
              href="#eligibility"
              data-testid="learn-cta-check"
              className="inline-flex items-center gap-1.5 h-11 px-5 bg-black text-white rounded-full text-sm font-semibold hover:bg-[#0055FF] transition-colors"
            >
              Check your eligibility <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#lenders"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-black"
            >
              Or compare lenders <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4 text-xs text-white/60">
          <div>© {new Date().getFullYear()} Offer Express · Loan Express</div>
          <a href="https://offer.express" className="hover:text-white inline-flex items-center gap-1">
            Visit Offer Express <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      </footer>

      {/* Mobile capture dialog — collects phone before we open the lender.
          hitch_user_id + phone are persisted by the dialog so subsequent
          lender clicks in the same session go direct (no re-prompt). */}
      <MobileCaptureDialog
        open={!!mobileCapture}
        onOpenChange={(v) => { if (!v) setMobileCapture(null); }}
        product={mobileCapture}
        onCaptured={async () => {
          if (mobileCapture) await doTrackedRedirect(mobileCapture);
          setMobileCapture(null);
        }}
      />

      {/* Deep-integration KYC dialog — only used when the user clicks the
          CreditSea Smart card. Submits full KYC to /api/creditsea/lead,
          which dedupes + pushes to CreditSea and returns a redirect URL. */}
      <CreditSeaSmartDialog
        open={smartOpen}
        onOpenChange={setSmartOpen}
        product={matches.find((p) => isCreditSeaSmart(p))}
        metaPixelId={matches.find((p) => isCreditSeaSmart(p))?.meta_pixel_id}
      />
    </div>
  );
}
